import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ErrorHandlerService } from '../core/error-handler';
import { UserService } from '../core/user';
import moment from 'moment-timezone';
import { AnalyticsService } from '../core/analytics';
import { UserLocation } from '../models/user/location.model';
import { EngageConstants } from './engage.constants';
import { environment } from '../../environments/environment';
import {
  AttendeeSummary,
  CampaignRecipient,
  EmailStatus,
  EventRecipientRsvpStat,
  EventStatus,
  RewardStatus
} from '../models/engage/CampaignRecipients.model';
import { RewardMetadataModel } from '../models/engage/reward-metadata.model';
import { Step } from '../shared/progress-stepper/progress-stepper.component';
import { CampaignDetailsService } from './campaign-details/campaign-details.service';
import { DigitalMarketingCampaignSource } from './dependencies/DigitalMarketingCampaignSource';
import {
  CampaignDetails,
  CampaignInputs,
  CampaignStatus,
  CampaignType,
  Notification
} from '../models/engage/campaign-details.model';
import { CampaignSource } from './dependencies/ICampaignSource';
import { CampaignTemplatesModel } from '../models/engage/campaign-templates.model';
import { DigitalMarketingTemplateSource, TemplateSource } from './dependencies/DigitalMarketingTemplateSource';
import { AudienceSource } from '../meet/dependencies/audience-source';
import { DigitalMarketingAudienceSource } from '../meet/dependencies/digital-marketing-audience-source';
import { CalculatedAudience } from '../models/meet/calculated-audience.model';
import { RewardAnalyticsModel } from '../models/engage/reward-analytics.model';

@Injectable()
export class EngageService {
  constructor(
    private readonly us: UserService,
    private readonly http: HttpClient,
    private readonly ehs: ErrorHandlerService,
    private readonly analytics: AnalyticsService,
    private readonly campaignDetailsService: CampaignDetailsService,
  ) { }

  private readonly loadingCampaignDetails: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  private readonly campaignSource: CampaignSource = new DigitalMarketingCampaignSource(this.http);
  private readonly templateSource: TemplateSource = new DigitalMarketingTemplateSource(this.http);
  private readonly audienceSource: AudienceSource = new DigitalMarketingAudienceSource(this.http);

  public isLoading(): Observable<boolean> {
    return this.loadingCampaignDetails.asObservable()
  }

  public getCalendarRewardDetails(id, type) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        const url = `/api/location/${loc.locationNumber}/calendar/reward/${id}/${type}`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      catchError(this.ehs.handleError)
    );
  }

  public getCalendarCampaignDetails(id) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        return this.getMappedCalendarCampaignDetails(id, loc)
      }),
      catchError(this.ehs.handleError)
    );
  }

  private getMappedCalendarCampaignDetails(id: string, loc: any): Observable<any> {
    return this.campaignSource.getCampaignDetails(loc.locationNumber, id).pipe(
      switchMap((response: CampaignDetails) => {
        this.analytics.sendEvent('Selected campaign CEV2 details', this.mapAnalyticsData(response));
        const template$ = this.getTemplate(response.templateID);
        const recipients$ = this.getCampaignRecipients(loc.locationNumber, response.recipients, response.audienceID, response);
        return forkJoin([of(response), template$, recipients$]);
      }),
      map(([response, template, recipientsObj]) => {
        this.campaignDetailsService.setCampaignRecipients(recipientsObj);
        return {
          campaignDetails: response,
          template,
          progressStepper: this.mapCalendarEventProgressStepper(response),
          attendeeSummary: recipientsObj.attendeeSummary
        };
      }),
      catchError(this.ehs.handleError)
    );
  }

  private mapAnalyticsData(response: CampaignDetails): any {
    const notification = response.notifications[0];
    return {
      ...response,
      notificationsSendDateTime: notification.sendDatetime,
      notificationsTemplateID: notification.templateID,
      notificationsSegment: notification.segment,
      notificationsChannel: notification.channel,
      emailCustomizationsSubject: notification.emailCustomizations.subject,
      emailCustomizationsBody: notification.emailCustomizations.body,
      emailCustomizationsRecruitmentUrl: notification.emailCustomizations.recruitmentUrl,
      themeID: notification.emailCustomizations.themeID,
      rewardStartDateTime: response.reward?.startDatetime,
      rewardEndDatetime: response.reward?.endDatetime,
      rewardId: response.reward?.rewardID,
      rewardAppMessage: response.reward?.appMessage,
      rewardIncludeOperatorImage: response.reward?.includeOperatorImage,
      rewardIsLimitedTimeOffer: response.reward?.isLimitedTimeOffer,
      rewardOfferBatchID: response.reward?.offerBatchID,
    };
  }

  public getCampaignRecipients(
    locationNumber: string,
    path: string,
    audienceId: string,
    campaign: CampaignDetails
  ): Observable<{ recipients: CampaignRecipient[], attendeeSummary: AttendeeSummary }> {
    return combineLatest([
      this.campaignSource.getCampaignRecipientStatuses(locationNumber, path),
      audienceId ? this.audienceSource.populateSingleCalculatedAudience(locationNumber, audienceId) : of({} as CalculatedAudience)
    ]).pipe(
      map(([response, audience]) => {
        const rewardStatuses = response.rewardStatuses;
        const emailStatuses = response.emailStatuses;
        const eventRSVPSummary = response.eventRSVPSummary;
        const attendeeSummary = response.attendeeSummary;
        const emailFailed = campaign.notifications.find(notification =>
          notification.layoutID === 'notification')?.creationStatus === CampaignStatus.Failed

        const goingMap = new Map(
          eventRSVPSummary.going.map(attendee => [attendee.cfaId, attendee])
        );

        let customerIds = audience?.customerIDs ?? response.campaignRecipients ?? [];
        const recipients = customerIds.map((cfaID: string) => {
          const rewardStatus = this.getRewardStatus(cfaID, rewardStatuses);
          const emailStatus = emailFailed ? EmailStatus.FailedProcessing : this.getEmailStatus(cfaID, emailStatuses, rewardStatus);
          const eventRecipientRsvpStat = this.getEventRecipientRsvpStat(cfaID, goingMap, eventRSVPSummary);
          return { cfaID, rewardStatus, emailStatus, eventRecipientRsvpStat };
        });
        return { recipients, attendeeSummary };
      }),
      catchError((err) => {
        console.error('Error fetching campaign recipients:', err);
        return of({ recipients: [], attendeeSummary: {} as AttendeeSummary });
      })
    );
  }

  public handleRecipientStatusesMultipleCampaigns(locationNumber: string, campaigns: CampaignDetails[]): Observable<any> {
    const campaignStatuses$ = forkJoin(
      campaigns.map(campaign =>
        this.campaignSource.getCampaignRecipientStatuses(locationNumber, campaign.recipients, campaign.id)
      )
    );

    const audienceData$ = this.audienceSource.populateCalculatedAudiences(
      locationNumber,
      campaigns.map(campaign => campaign.audienceID)
    );

    return combineLatest([campaignStatuses$, audienceData$]).pipe(
      map( ([recipientsResponses, audiences]) => {
        const mappedStatuses = new Map(recipientsResponses.map(
          response => [response.campaignID, response]
        ))
        let recipients = []
        const sortedCampaigns = [...campaigns].sort((a,b) =>
          moment(a.reward.startDatetime).isBefore(b.reward.startDatetime) ? -1 : 1
        )
        const seenStatuses = new Map<string, RewardStatus>();
        const hiddenStatuses = new Set([RewardStatus.Pending, RewardStatus.DuplicateRewardExclusion, RewardStatus.LocationRewardExclusion])

        sortedCampaigns.forEach(campaign => {
          let customerIds = audiences.find(aud => aud.audienceID === campaign.audienceID)?.customerIDs.map((cfaID: string) => ({
            cfaID,
            rewardStatus: this.getRewardStatus(cfaID, mappedStatuses.get(campaign.id).rewardStatuses ?? {}),
            emailStatus: this.getEmailStatus(cfaID, mappedStatuses.get(campaign.id).emailStatuses ?? {},
              this.getRewardStatus(cfaID, mappedStatuses.get(campaign.id).rewardStatuses ?? {})),
            dates: campaign.reward.startDatetime.format('MMM D') +
                '-' + campaign.reward.endDatetime.format('MMM D, YYYY'),
            eventRecipientRsvpStat: { status: EventStatus.NotGoing },
          })) ?? [];
          customerIds = customerIds.filter(c => {
            const lastStatus = seenStatuses.get(c.cfaID)
            seenStatuses.set(c.cfaID, c.rewardStatus);
            return !lastStatus || !hiddenStatuses.has(c.rewardStatus)
          });
          recipients = recipients.concat(customerIds);
        })
        return { recipients, attendeeSummary: {} as AttendeeSummary }
      }),
      catchError( error => {
        console.error('Error fetching campaign recipients for automation:', error);
        return of({ recipients: [], attendeeSummary: {} as AttendeeSummary })
      })
    );

  }

  private combineStatuses(responses: any[]): { emailStatuses: any; rewardStatuses: any } {
    const combined = { emailStatuses: {}, rewardStatuses: {}, eventRSVPSummary:{}, attendeeSummary:{} };
    responses.forEach(response => {
      combined.emailStatuses = this.mergeStatuses(response.emailStatuses, combined.emailStatuses);
      combined.rewardStatuses = this.mergeStatuses(response.rewardStatuses, combined.rewardStatuses);
      combined.eventRSVPSummary = this.mergeStatuses(response.eventRSVPSummary, combined.eventRSVPSummary);
      combined.attendeeSummary = response.attendeeSummary;
    });
    return combined;
  }

  private mergeStatuses(source: Record<string,Set<any>>, target: Record<string,Set<any>>): any {
    Object.entries(source).forEach(([key, value]) => {
      target[key] = target[key] ? new Set([...target[key], ...value]) : new Set(value);
    });
    return target;
  }

  private getRewardStatus(cfaID: string, rewardStatuses: any): RewardStatus {
    if (rewardStatuses.duplicateRewardExclusion.has(cfaID)) {
      return RewardStatus.DuplicateRewardExclusion;
    } else if (rewardStatuses.locationRewardExclusion.has(cfaID)) {
      return RewardStatus.LocationRewardExclusion;
    } else if (rewardStatuses.rewardCreationError.has(cfaID)) {
      return RewardStatus.Failed;
    } else if (rewardStatuses.redeemedAtIssuedLocation.has(cfaID)) {
      return RewardStatus.RedeemedIssuedLocation;
    } else if (rewardStatuses.redeemedAtOtherLocation.has(cfaID)) {
      return RewardStatus.RedeemedOtherLocation;
    } else if (rewardStatuses.loaded.has(cfaID)) {
      return RewardStatus.Loaded;
    }
    return RewardStatus.Pending;
  }

  private getEmailStatus(cfaID: string, emailStatuses: any, rewardStatus: RewardStatus): EmailStatus {
    if (
      rewardStatus === RewardStatus.Failed ||
      rewardStatus === RewardStatus.DuplicateRewardExclusion ||
      rewardStatus === RewardStatus.LocationRewardExclusion
    ) {
      return EmailStatus.NotAttempted;
    } else if (emailStatuses.unsubscribed.has(cfaID)) {
      return EmailStatus.Unsubscribed;
    } else if (emailStatuses.spam.has(cfaID)) {
      return EmailStatus.Spam;
    } else if (emailStatuses.opened.has(cfaID)) {
      return EmailStatus.Opened;
    } else if (emailStatuses.failedProcessing.has(cfaID)) {
      return EmailStatus.FailedProcessing;
    } else if (emailStatuses.failed.has(cfaID)) {
      return EmailStatus.Failed;
    } else if (emailStatuses.dropped.has(cfaID)) {
      return EmailStatus.Dropped;
    } else if (emailStatuses.delivered.has(cfaID)) {
      return EmailStatus.Delivered;
    } else if (emailStatuses.sent.has(cfaID)) {
      return EmailStatus.Sent;
    }
    return EmailStatus.Pending;
  }

  private getEventRecipientRsvpStat(cfaID: string, goingMap: Map<string, any>, eventRSVPSummary: any): EventRecipientRsvpStat {
    const goingAttendee = goingMap.get(cfaID);
    if (goingAttendee) {
      return {
        status: EventStatus.Going,
        adultsAttending: goingAttendee.adultsAttending,
        kidsAttending: goingAttendee.kidsAttending,
      };
    } else if (eventRSVPSummary.notGoing.has(cfaID)) {
      return { status: EventStatus.NotGoing };
    } else if (eventRSVPSummary.maybe.has(cfaID)) {
      return { status: EventStatus.Maybe };
    }
    return { status: EventStatus.Maybe };
  }

  public getCalendarAutomatedRewardDetails(treatGroupIds) {
    let groupIds = {
      'treatBatchIds': treatGroupIds
    };
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        const url = `/api/location/${loc.locationNumber}/calendar/reward/automation`;
        return this.http.post(environment.spotlightApiUrl + url, groupIds);
      }),
      map((res: any) => res.responseObject),
      catchError(this.ehs.handleError)
    );
  }

  public getCalendarEventDetails(id) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        const url = `/api/location/${loc.locationNumber}/event/${id}`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      catchError(this.ehs.handleError)
    );
  }

  public getCalendarEmailDetails(id) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        const url = `/api/location/${loc.locationNumber}/emailStats/${id}`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      catchError(this.ehs.handleError)
    );
  }

  public getCalendarBonusPointDetails(id) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        const url = `/api/location/${loc.locationNumber}/promotion/${id}`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => { return res.responseObject }),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public getCalendarBonusPointStats(id) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        const statsUrl = `/api/location/${loc.locationNumber}/promotionStats/${id}`;
        return this.http.get(environment.spotlightApiUrl + statsUrl);
      }),
      map((res: any) => { return res.responseObject }),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public getCalendarCommunityCareEvent(id) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        const customerUrl = `/api/location/${loc.locationNumber}/cceStats?eventId=${id}`;
        const eventUrl = `/api/location/${loc.locationNumber}/communityCareEvent?eventId=${id}`;
        return forkJoin([this.http.get(environment.spotlightApiUrl + customerUrl), this.http.get(environment.spotlightApiUrl + eventUrl)])
      }),
      map((res: any) => { return { ...res[0], ...res[1] } }),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    )
  }

  public getCalendarThirdPartyPromotion(id) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        const promoUrl = `/api/location/${loc.locationNumber}/thirdPartyPromotion/${id}`;
        const metaDataUrl = `/api/${loc.locationNumber}/engagementDetails?automationDetails=${false}`;
        const metaDataRequest = {
          'campaignName': 'Grow Third Party Delivery',
          'engagementType': 'third-party-promotion',
          'flowId': 'third-party-promotion',
          'emailMessage': '',
          'selectedCustomers': [],
          'subject': ''
        }
        return forkJoin([this.http.get(environment.spotlightApiUrl + promoUrl),
          this.http.post(environment.spotlightApiUrl + metaDataUrl, metaDataRequest)]);
      }),
      map((responses: any) => responses.map(response => response.responseObject)),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public getCalendarFreeItemInCartPromotion(id) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        const promoUrl = `/api/location/${loc.locationNumber}/promotion/${id}`;
        const metaDataUrl = `/api/${loc.locationNumber}/engagementDetails?automationDetails=${false}`;
        const metaDataRequest = {
          'campaignName': 'Grow Third Party Delivery',
          'engagementType': 'free-item-in-cart-promotion',
          'flowId': 'free-item-in-cart-promotion',
          'emailMessage': '',
          'selectedCustomers': [],
          'subject': ''
        }
        return forkJoin([this.http.get(environment.spotlightApiUrl + promoUrl),
          this.http.post(environment.spotlightApiUrl + metaDataUrl, metaDataRequest)]);
      }),
      map((responses: any) => responses.map(response => response.responseObject)),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public getCalendarThirdPartyPromotionStats(id) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        const statsUrl = `/api/location/${loc.locationNumber}/thirdPartyPromotion/stats/${id}`;
        return this.http.get(environment.spotlightApiUrl + statsUrl);
      }),
      map((response: any) => response.responseObject),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public getCalendarFreeInCartStats(id) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc) => {
        const statsUrl = `/api/location/${loc.locationNumber}/promotion/${id}/stats`;
        return this.http.get(environment.spotlightApiUrl + statsUrl);
      }),
      map((response: any) => response.responseObject),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public getRewardTemplateDetails() {
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        const { locationNumber } = location;
        const url = `/api/location/${locationNumber}/reward/template/metadata`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public getTemplate(templateID: string) {
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        return this.templateSource.getTemplateDetails(location.locationNumber, templateID)
      }),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public getCampaignRewardAnalytics(campaignID: string): Observable<RewardAnalyticsModel | null> {
    return this.us.getSelectedLocation().pipe(
      switchMap(({ locationNumber }) => this.http.get<{
        campaign: {
          endDatetime: string;
          rewardMetrics: {
            issuedCount: number;
            totalredeemedCount: number;
            locationredeemedCount: number;
            otherlocationredeemedCount: number;
            locationredeemedTxnamtZeroCount: number;
            otherlocationredeemedTxnamtZeroCount: number;
            salesAmount: number;
            locationSalesAmount: number;
          };
        };
      }>(`${environment.eventMetricsUrl}/api/reward-metrics/${encodeURIComponent(campaignID)}`, {
        headers: new HttpHeaders({ 'Requested-Localities': locationNumber })
      })),
      map(({ campaign }) => {
        if (moment(campaign.endDatetime).isBefore(moment().subtract(13, 'months'))) {
          return null;
        }
        const metrics = campaign.rewardMetrics;
        const ratio = (numerator: number, denominator: number) => denominator > 0 ? numerator / denominator : 0;
        const locationSalesAmount = metrics.locationSalesAmount ?? 0;
        return {
          totalOffersIssued: metrics.issuedCount,
          totalOffersRedeemed: metrics.totalredeemedCount,
          totalRedeemedSameLocation: metrics.locationredeemedCount,
          totalRedeemedOtherLocations: metrics.otherlocationredeemedCount,
          sameLocOffersRedeemPct: ratio(metrics.locationredeemedCount, metrics.issuedCount),
          totalSalesAttached: metrics.salesAmount,
          totalSalesAttachedLocation: locationSalesAmount,
          totalCheckAverage: ratio(metrics.salesAmount, metrics.totalredeemedCount),
          totalCheckAverageLocation: ratio(locationSalesAmount, metrics.locationredeemedCount),
          percentZeroTxn: ratio(metrics.locationredeemedTxnamtZeroCount + metrics.otherlocationredeemedTxnamtZeroCount,
            metrics.totalredeemedCount),
          percentZeroTxnLocation: ratio(metrics.locationredeemedTxnamtZeroCount, metrics.locationredeemedCount),
          recUpdatedAt: null
        };
      }),
      tap((analytics) => this.analytics.sendEvent('Reward Campaign Analytics Loaded', {
        'Campaign ID': campaignID,
        'Source': 'Metrics Service',
        'Has Redemption Data': analytics !== null
      })),
      catchError((error) => {
        this.analytics.sendEvent('Reward Campaign Analytics Failed', {
          'Campaign ID': campaignID,
          'Source': 'Metrics Service'
        });
        return this.ehs.handleError(error);
      })
    );
  }

  public getRewardAnalytics(batchId: string) {
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        const { locationNumber } = location;
        const campaignType = 'rewards';
        const url = `/api/location/${locationNumber}/${campaignType}/analytics/${batchId}`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public getTreats() {
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        const { locationNumber } = location;
        const url = `/api/location/${locationNumber}/awardTemplates`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public getCampaignTemplates(type: CampaignType): Observable<CampaignTemplatesModel[]> {
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        const { locationNumber } = location;
        const headers = new HttpHeaders({
          'Requested-Localities': locationNumber
        });
        return this.http.get(environment.digitalMarketingCampaignsServiceURL + '/api/restaurant/templates?type='
          + type.toString(), { headers: headers });
      }),
      map((res: CampaignTemplatesModel[]) => res),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public getEventTemplates() {
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        const { locationNumber } = location;
        const url = `/api/location/${locationNumber}/events`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject.centerStageEvents),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public getTemplateDetails(templateId) {
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        const { locationNumber } = location;
        const url = `/api/location/${locationNumber}/email/templates/${templateId}`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public getRewardDetails(rewardId: string): Observable<RewardMetadataModel> {
    const url = `/api/rewardDetails?offerId=${rewardId}`;
    return this.http.get(environment.spotlightApiUrl + url).pipe(
      map((res: any) => {
        return res.responseObject
      }),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      }));
  }

  public loadFilters() {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/${locationNumber}/default-filters`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res),
      catchError(this.ehs.handleError)
    );
  }

  public getEventTemplateDetails() {
    const eventTemplateHtml = this.getTemplateDetails(EngageConstants.eventHtmlId);
    const eventTemplateRSVP = this.getTemplateDetails(EngageConstants.eventRsvpHtmlId);
    const eventTemplateNoRSVP = this.getTemplateDetails(EngageConstants.eventNoRsvpHtmlId);

    const templateCalls = forkJoin([eventTemplateHtml, eventTemplateRSVP, eventTemplateNoRSVP]);
    return templateCalls.pipe(map((templateHtmls) => {
      return {
        previewHtml: templateHtmls[0].previewHtml,
        rsvpHtml: templateHtmls[1].previewHtml,
        noRsvpHtml: templateHtmls[2].previewHtml
      };
    }));
  }

  public sendReward(reward) {
    this.analytics.sendEvent(
      'Reward Sent',
      {
        rewardName: reward.offerName,
        rewardLength: reward.endDate ? reward.endDate.diff(reward.startDate, 'days') + 1 : null,
        numberOfCustomers: reward.numberOfCustomers,
        numberOfFilters: reward.selectedFilters.length,
        numberOfSavedGroups: reward.selectedSavedGroups.length,
        endDate: reward.endDate.format(),
        startDate: reward.startDate.format(),
        includedFilters: reward.selectedFilters.map(filter => filter.id).toString(),
        includedGroups: reward.selectedSavedGroups.map(group => group.name).toString(),
        recruitmentModuleEnabled: reward.recruitmentModuleEnabled,
        recruitmentModuleLink: reward.recruitmentModuleLink,
        includeActive: reward.includeActive,
        campaignEngineV2: reward.campaignEngineV2,
        allFilters: reward.allFilters.map(filter => filter.id).toString()
      }
    );
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        let mappedReward = this.mapReward(reward, location.locationNumber, reward.campaignEngineV2);
        if (reward.campaignEngineV2) {
          const { locationNumber } = location;
          const headers = new HttpHeaders({
            'Requested-Localities': locationNumber
          });
          return this.http.post(environment.digitalMarketingCampaignsServiceURL + `/api/restaurant/campaigns`,
            mappedReward, { headers: headers });
        } else {
          const { locationNumber } = location;
          const url = `/api/location/${locationNumber}/sendOffer`;
          return this.http.post(environment.spotlightApiUrl + url, mappedReward);
        }

      }),
      map((res: any) => res.responseObject ?? res),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public createCampaign(campaign: CampaignInputs, audienceFilterAnalytics = {}) {
    this.analytics.sendEvent(
      'Campaign Engine v2 campaign creation',
      {
        ...campaign.rewardInformation,
        ...campaign.customizations,
        ...campaign.template,
        ...audienceFilterAnalytics,
      }
    );
    return this.us.getSelectedLocation().pipe(
      switchMap((location: UserLocation) => this.campaignSource.createCampaign(location.locationNumber, campaign)),
      catchError((error: any) => this.ehs.handleError(error))
    )
  }

  public updateCampaign(campaign: CampaignInputs) {
    this.analytics.sendEvent(
      'Campaign Engine v2 campaign update',
      {
        ...campaign.rewardInformation,
        ...campaign.customizations,
        ...campaign.template,
      }
    );
    if (campaign.template.campaignType === CampaignType.Event && campaign.sendingOptions.sendDate.isBefore(moment())) {
      campaign.eventInformation.sendUpdateNotification = true;
    }
    return this.us.getSelectedLocation().pipe(
      switchMap((location: UserLocation) => this.campaignSource.updateCampaign(location.locationNumber, campaign)),
      catchError((error: any) => this.ehs.handleError(error))
    )
  }

  public resendReward(reward) {
    this.analytics.sendEvent(
      'Reward Sent',
      {
        rewardName: reward.offerName,
        rewardLength:
          reward.endDate ? reward.endDate.diff(reward.startDate, 'days') + 1 : null,
        numberOfIndividualRecipients: reward.cfaIds.length,
        numberOfDefaultGroups: reward.includedGroups.length,
        numberOfSavedFilters: reward.includedFilters.length,
        defaultGroups: reward.includedGroups.join(),
        endDate: reward.endDate.format(),
        startDate: reward.startDate.format(),
        recruitmentModuleEnabled: reward.recruitmentModuleEnabled,
        recruitmentModuleLink: reward.recruitmentModuleLink,
        includeActive: reward.includeActive,
      }
    );
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        let mappedReward = this.mapResendReward(reward, location.locationNumber);
        const { locationNumber } = location;
        const url = `/api/location/${locationNumber}/sendOffer`;
        return this.http.post(environment.spotlightApiUrl + url, mappedReward);
      }),
      map((res: any) => res.responseObject),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public sendEvent(event) {
    this.analytics.sendEvent(
      'Event Sent',
      {
        templateName: event.templateId,
        numberOfCustomers: event.numberOfCustomers,
        numberOfFilters: event.selectedFilters.length,
        numberOfSavedGroups: event.selectedSavedGroups.length,
        recruitmentModuleEnabled: event.recruitmentModuleEnabled,
        recruitmentModuleLink: event.recruitmentModuleLink,
        includedFilters: event.selectedFilters.map(filter => filter.id).toString(),
        includedGroups: event.selectedSavedGroups.map(group => group.name).toString(),
        allFilters: event.allFilters.map(filter => filter.id).toString()
      }
    );

    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        const { locationNumber } = location;
        const url = `/api/location/${locationNumber}/calendarEvent`;
        return this.http.post(environment.spotlightApiUrl + url, event);
      }),
      map((res: any) => res.responseObject),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public sendEmail(email) {
    this.analytics.sendEvent(
      'Email Sent',
      {
        templateName: email.templateName,
        numberOfCustomers: email.numberOfCustomers,
        numberOfFilters: email.selectedFilters.length,
        numberOfSavedGroups: email.selectedSavedGroups.length,
        recruitmentModuleEnabled: email.recruitmentModuleEnabled,
        recruitmentModuleLink: email.recruitmentModuleLink,
        includedFilters: email.selectedFilters.map(filter => filter.id).toString(),
        includedGroups: email.selectedSavedGroups.map(group => group.name).toString(),
        allFilters: email.allFilters.map((filter) => filter.id).toString()
      }
    );

    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        const { locationNumber } = location;
        const url = `/api/location/${locationNumber}/email`;
        return this.http.post(environment.spotlightApiUrl + url, email);
      }),
      map((body: any) => body.responseObject),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public cancelReward(reward) {
    this.analytics.sendEvent(
      'Reward Canceled',
      {
        rewardName: reward.offerName,
        rewardRecipients: reward.showCustomers ? reward.recipients.length : null
      }
    );
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        const { locationNumber } = location;
        const url = `/api/location/${locationNumber}/cancelReward/${reward.batchId}`;
        return this.http.delete(environment.spotlightApiUrl + url);
      }),
      map((body) => body),
      catchError(this.ehs.handleError)
    );
  }

  public cancelCurrentReward(id: string) {
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        this.analytics.sendEvent(
          'Canceled Current Reward CEv2 ',
          {
            id: id,
          }
        );
        const headers = new HttpHeaders({
          'Requested-Localities': location.locationNumber
        });

        return this.http.delete(environment.digitalMarketingCampaignsServiceURL +
                    `/api/restaurant/campaigns/${id}/reward`, { headers: headers });
      }),
      catchError(this.ehs.handleError)
    );
  }

  public cancelCampaign(payload: any, campaign: CampaignInputs) {
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        this.analytics.sendEvent(
          'Canceled Campaign CEv2 ',
          {
            id: campaign.campaignID,
            templateName: campaign.template.title,
            name: campaign.customizations.name,
          }
        );

        const headers = new HttpHeaders({
          'Requested-Localities': location.locationNumber
        });

        const shouldSendCancellationNotification =
          campaign.template.campaignType === CampaignType.Event &&
          campaign.sendingOptions.sendDate.isBefore(moment());

        if (shouldSendCancellationNotification) {
          campaign.eventInformation.sendCancellationNotification = true;
          return this.campaignSource.updateCampaign(location.locationNumber, campaign).pipe(
            switchMap(() =>
              this.http.delete(environment.digitalMarketingCampaignsServiceURL +
                `/api/restaurant/campaigns/${campaign.campaignID}?hard=false`, { headers: headers })
            )
          );
        }

        return this.http.delete(environment.digitalMarketingCampaignsServiceURL +
          `/api/restaurant/campaigns/${campaign.campaignID}?hard=false`, { headers: headers });
      }),
      catchError(this.ehs.handleError)
    );
  }

  public cancelEvent(event) {
    let eventRecipientCount = null;

    if (event.showCustomers) {
      eventRecipientCount = event.recipientIds.length;
    }
    this.analytics.sendEvent(
      'Event Canceled',
      {
        eventName: event.title,
        eventRecipients: eventRecipientCount
      }
    );
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        const { locationNumber } = location;
        const url = `/api/location/${locationNumber}/calendarEvent/${event.id}`;
        return this.http.delete(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      catchError(this.ehs.handleError)
    );
  }

  public cancelEmail(email) {
    let emailCustomerCount = null;

    if (email.showCustomers) {
      emailCustomerCount = email.cfaIds.length;
    }
    this.analytics.sendEvent(
      'Email Canceled',
      {
        emailTemplate: email.templateTitle,
        emailRecipients: emailCustomerCount
      }
    );
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        const { locationNumber } = location;
        const url = `/api/location/${locationNumber}/email/${email.id}`;
        return this.http.delete(environment.spotlightApiUrl + url);
      }),
      map((body) => body),
      catchError(this.ehs.handleError)
    );
  }

  public editEvent(event) {
    this.analytics.sendEvent(
      'Event Edited',
      {
        emailTemplate: event.templateTitle,
        numberOfFilters: event.selectedFilters.length,
        numberOfSavedGroups: event.selectedSavedGroups.length
      }
    );
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        const { locationNumber } = location;
        const url = `/api/location/${locationNumber}/calendarEvent`;
        return this.http.put(environment.spotlightApiUrl + url, event);
      }),
      map((body) => body),
      catchError(this.ehs.handleError)
    );
  }

  public editEmail(email) {
    this.analytics.sendEvent(
      'Email Edited',
      {
        emailTemplate: email.templateTitle,
        numberOfFilters: email.selectedFilters.length,
        numberOfSavedGroups: email.selectedSavedGroups.length
      }
    );
    return this.us.getSelectedLocation().pipe(
      switchMap((location) => {
        const { locationNumber } = location;
        const url = `/api/location/${locationNumber}/email`;
        return this.http.put(environment.spotlightApiUrl + url, email);
      }),
      map((body: any) => body.responseObject),
      catchError(this.ehs.handleError)
    );
  }

  public getSavedFilters() {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let url = `/api/location/${loc.locationNumber}/savedFiltersForEngage`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      catchError(this.ehs.handleError)
    );
  }

  public getSavedFilterCustomers(filterId) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let url = `/api/location/${loc.locationNumber}/filter/${filterId}`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      catchError(this.ehs.handleError)
    );
  }

  public getFilterDisplayCustomers(req) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let url = `/api/location/${loc.locationNumber}/filters/customers`;
        return this.http.post(environment.spotlightApiUrl + url, req);
      }),
      map((res: any) => res.responseObject || {}),
      catchError(this.ehs.handleError)
    );
  }

  public getCustomersByIds(ids, limitSize: boolean = true) {
    // We currently are not able to display a location's full customer list at once and have capped it at displaying
    // 50 customers in addition to the 10 we show in the initial filter chips.
    // They are still able to view the full customer list incrementally by clicking through the customer table
    if (ids == undefined) {
      return of({ customerProfiles: [] });
    }

    ids = ids.length > 60 && limitSize ? ids.slice(0, 60) : ids;

    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let url = `/api/location/${loc.locationNumber}/customersById`;
        return this.http.post(environment.spotlightApiUrl + url, ids);
      }),
      map((res: any) => res.responseObject || {}),
      catchError(this.ehs.handleError)
    );
  }

  private mapReward(reward, locationNumber, campaignEngineV2) {
    if (campaignEngineV2) {
      let startDate = moment(reward.startDate).isSameOrBefore(moment()) ?
        moment() : moment(reward.startDate).set('hour', 5)
      let endDate = moment(reward.endDate).set('hour', 23)
      this.analytics.sendEvent(
        'Reward request for Campaign Engine V2',
        {
          recipients: reward.totalCustomers,
          name: reward.campaignName,
          title: reward.offerName,
          notificationsSendDateTime: startDate,
          notificationsTemplateID: 'spotlight:campaign:reward',
          notificationsSegment: 'CAMPAIGN',
          notificationsChannel: 'EMAIL',
          emailCustomizationsSubject: reward.subject,
          emailCustomizationsBody: reward.emailCopy,
          emailCustomizationsHeaderImageUrl: reward.image ?? reward.headerImage,
          emailCustomizationsMobileHeaderImageUrl: reward.image ?? reward.mobileHeaderImage,
          emailCustomizationsPreHeader: reward.subTitle ?? reward.headline,
          emailCustomizationsRecruitmentUrl: reward.recruitmentModuleEnabled ? reward.recruitmentModuleLink : null,
          type: 'REWARD',
          rewardStartDateTime: startDate,
          rewardEndDatetime: endDate,
          rewardId: reward.id ?? reward.treatId,
          rewardAppMessage: reward.defaultMessageForApp,
          rewardIncludeOperatorImage: reward.sendOperatorImage,
        }
      );
      return {
        recipients: reward.totalCustomers,
        name: reward.campaignName,
        title: reward.offerName,
        notifications: [
          {
            sendDateTime: startDate,
            emailCustomizations: {
              subject: reward.subject,
              body: reward.emailCopy,
              themeID: reward.themeId,
              recruitmentUrl: reward.recruitmentModuleEnabled ? reward.recruitmentModuleLink : null
            },
            templateID: 'spotlight:campaign:reward',
            segment: 'CAMPAIGN',
            channel: 'EMAIL'
          }
        ],
        type: 'REWARD',
        reward: {
          includeActiveLocationRewardCustomers: reward.includeActive,
          startDatetime: startDate,
          endDatetime: endDate,
          isLimitedTimeOffer: reward.limitedtimeoffer,
          rewardId: reward.id ?? reward.treatId,
          appMessage: reward.defaultMessageForApp,
          includeOperatorImage: reward.sendOperatorImage
        }
      }
    }
    return {
      awardId: reward.id || reward.treatId,
      breakfastDisclaimerText: reward.breakfastDisclaimerText,
      startDateLiteral: moment(reward.startDate).format('YYYY-MM-DD'),
      endDateLiteral: moment(reward.endDate).format('YYYY-MM-DD'),
      endDateCountdownTimer: reward.endDateCountdownTimer,
      headerImage: reward.image || reward.headerImage,
      mobileHeaderImage: reward.mobileImage,
      headline: reward.subTitle || reward.headline,
      locationNumber,
      name: reward.name || reward.offerName,
      campaignName: reward.campaignName,
      offerCategory: reward.category || reward.treatCategory,
      offerImage: reward.imageURL || reward.offerImage,
      sendOperatorImage: reward.sendOperatorImage,
      subject: reward.subject,
      type: 'reward',
      recruitmentModuleEnabled: reward.recruitmentModuleEnabled,
      recruitmentModuleLink: reward.recruitmentModuleLink,
      treatCategory: reward.category,
      treatMessageForApp: reward.defaultMessageForApp,
      validDetails: reward.details || reward.validDetails,
      offerDetails: reward.emailCopy,
      resentNewTreat: reward.resentNewTreat,
      resentSameTreat: reward.resentSameTreat,
      earlierBatchId: reward.earlierBatchId,
      countdownTimer: reward.showCountdownTimer !== 'style="display: none;"',
      breakfast: reward.breakfast,
      limitedtimeoffer: reward.limitedtimeoffer,
      dynamicFlag: reward.dynamicFlag,
      selectedTheme: reward.selectedTheme,
      themeId: reward.themeId,
      includeActiveCustomers: reward.includeActive,
      selectedFilters: reward.selectedFilters,
      selectedSavedGroups: reward.selectedSavedGroups
    };
  }

  private mapResendReward(reward, locationNumber) {
    return {
      awardId: reward.id || reward.treatId,
      breakfastDisclaimerText: reward.breakfastDisclaimerText,
      startDateLiteral: moment(reward.startDate).format('YYYY-MM-DD'),
      endDateLiteral: moment(reward.endDate).format('YYYY-MM-DD'),
      endDateCountdownTimer: reward.endDateCountdownTimer,
      headerImage: reward.image || reward.headerImage,
      mobileHeaderImage: reward.mobileImage,
      headline: reward.subTitle || reward.headline,
      locationNumber,
      name: reward.name || reward.offerName,
      campaignName: reward.campaignName,
      offerCategory: reward.category || reward.treatCategory,
      offerImage: reward.imageURL || reward.offerImage,
      cfaIds: reward.cfaIds,
      includedGroups: reward.includedGroups,
      includedFilters: reward.includedFilters,
      sendOperatorImage: reward.sendOperatorImage,
      subject: reward.subject,
      type: 'reward',
      recruitmentModuleEnabled: reward.recruitmentModuleEnabled,
      recruitmentModuleLink: reward.recruitmentModuleLink,
      treatCategory: reward.category,
      treatMessageForApp: reward.defaultMessageForApp,
      validDetails: reward.details || reward.validDetails,
      offerDetails: reward.emailCopy,
      resentNewTreat: reward.resentNewTreat,
      resentSameTreat: reward.resentSameTreat,
      earlierBatchId: reward.earlierBatchId,
      countdownTimer: reward.showCountdownTimer !== 'style="display: none;"',
      breakfast: reward.breakfast,
      limitedtimeoffer: reward.limitedtimeoffer,
      dynamicFlag: reward.dynamicFlag,
      selectedTheme: reward.selectedTheme,
      themeId: reward.themeId,
      includeActiveCustomers: reward.includeActive
    };
  }

  private mapCalendarEventProgressStepper(campaignDetails: CampaignDetails) {
    const sendProgress: Step[] = [
      {
        stepName: 'Scheduled',
        isCompleted: true,
      }
    ]
    if (campaignDetails.reward) {
      const isRewardSent: boolean = campaignDetails.reward.offerBatchID !== undefined
        || campaignDetails.notifications[0].creationStatus == 'COMPLETED'
      const rewardSendFailure: string = !isRewardSent && campaignDetails.status == 'FAILED'
        ? 'Reward processing failed. No rewards were loaded into customer accounts.' : null
      sendProgress.push({ stepName: 'Reward Processed', isCompleted: isRewardSent, error: rewardSendFailure })
    }
    campaignDetails.notifications.forEach((notification: Notification) => {
      const isNotificationSent: boolean = notification.creationStatus == 'COMPLETED'
      const notificationSendFailure: string = notification.creationStatus == 'FAILED' || campaignDetails.status == 'FAILED'
        ? 'Email processing failed. No attempts could be made to send emails.' : null
      sendProgress.push({ stepName: 'Email Processed', isCompleted: isNotificationSent, error: notificationSendFailure })
    })
    if (campaignDetails.reward?.deletedAt) {
      sendProgress.push({ stepName: 'Cancelled', isCompleted: false, error: null, info: true })
    }
    return sendProgress;
  }

}
